import { Injectable } from '@angular/core';
import { BackendService } from './backend.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { MatSnackBar } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';

/**
 * Login Data model
 */
export interface LoginData {
  /**
   * Status
   */
  status: string;
  /**
   * Message
   */
  message: string;
  /**
   * Data
   */
  token: string;
}

/**
 * Service that handles the authorization of users
 */
@Injectable()
export class AuthService {
  /**
   * JWT Authentication token
   */
  private token = '';

  public loggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Constructor
   */
  constructor(private backendService: BackendService, private snackBar: MatSnackBar, private translate: TranslateService) {
    this.token = localStorage.getItem('UserToken') || '';

    this.loggedIn.next(this.isLoggedIn());
  }

  /**
   * Checks if user is logged in
   * @returns {boolean} True if user is logged in
   */
  public isLoggedIn() {
    return this.token !== '' && this.token !== null;
  }

  /**
   * Logs in user with given email and password
   * @param {string} email Email
   * @param {string} password Password
   * @returns {Promise<any>} Response as Promise
   */
  login(email: string, password: string): Promise<any> {
    return this.backendService
      .post<LoginData>('auth/login', {
        email: email,
        password: password,
      })
      .then((response) => {
        localStorage.setItem('UserToken', response.token);
        this.token = response.token || '';

        this.loggedIn.next(this.isLoggedIn());

        return response;
      });
  }

  /**
   * Log in an user using an auth token
   * @param {string} token
   */
  loginWithToken(token: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.backendService
        .post<LoginData>(`auth/login/${token}`)
        .then((response) => {
          if (response.token) {
            localStorage.setItem('UserToken', response.token);
            this.token = response.token || '';

            this.loggedIn.next(this.isLoggedIn());

            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch((error) => {
          this.translate.get('LOGIN.authTokenLoginError').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
          reject(error);
        });
    });
  }

  /**
   * Logs out user
   */
  logout(): void {
    this.token = null;
    this.loggedIn.next(this.isLoggedIn());
    localStorage.removeItem('UserToken');
  }

  /**
   * Step 1 / 3
   * Sends a registration request for the specified email adress with the given password, language preference and activation token.
   * This will send a confirmation email to the users email address.
   * @param email
   * @param password
   * @param lan
   * @param token
   * @param boToken
   * @param selectTeamAfterRegistration - Whether user should be redirected to team selection after first login
   */
  registerUser(
    email: string,
    password: string,
    lan: string,
    token: string,
    boToken: string,
    selectTeamAfterRegistration?: boolean,
  ): Promise<any> {
    const body: any = {
      email: email,
      password: password,
      language: lan,
    };

    if (selectTeamAfterRegistration) {
      body.selectTeamAfterRegistration = true;
    }

    return this.backendService.post(`auth/register/${token}/${boToken}`, body);
  }

  /**
   * requests an activation email to be resend for an already registered but inactive user
   * @param email
   */
  resendActivationEmail(email: string) {
    return this.backendService.post('auth/activation/mail', { email: email });
  }

  /**
   * Sends a verification request for a given confirmation token, obtained through a registration email sent in step 1.
   * This should return all relevant information to complete the next step. (Company and language Details)
   * @param token
   */
  getCompanyByVerificationToken(token: string): Promise<any> {
    return this.backendService.get('companies/' + token);
  }

  /**
   * Activates a user identified by the token sent in step 1/2. Also sets some of the user's settings.
   * @param branchofficeID
   * @param gender
   * @param firstName
   * @param lastName
   * @param token
   */
  activateAccount(branchofficeID: string, gender: boolean, firstName: string, lastName: string, token: string): Promise<any> {
    return this.backendService
      .post('auth/activate/' + token, {
        branchofficeId: branchofficeID,
        gender: gender,
        firstName: firstName,
        lastName: lastName,
      })
      .then((response: { token: string; challengeID?: string }) => {
        localStorage.setItem('UserToken', response.token);
        this.token = response.token || '';

        this.loggedIn.next(this.isLoggedIn());

        return response;
      });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/shared/services/auth.service.ts